import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { usePlanner } from '../context/PlannerContext';
import { analyzeStudentSkills, createGroups } from '../utils/skillAnalysis';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiArrowRight, FiSettings, FiTarget, FiUsers, FiEdit3, FiShoppingBag } = FiIcons;

function GroupPlanner() {
  const navigate = useNavigate();
  const { state, dispatch } = usePlanner();
  const [groupSettings, setGroupSettings] = useState(state.groupSettings);
  const [analyzedStudents, setAnalyzedStudents] = useState([]);
  const [suggestedGroups, setSuggestedGroups] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(true);

  useEffect(() => {
    if (!state.students.length) {
      navigate('/assessment');
      return;
    }

    // Analyze student skills
    setTimeout(() => {
      const analyzed = analyzeStudentSkills(state.students, state.assessmentType);
      setAnalyzedStudents(analyzed);
      const groups = createGroups(analyzed, groupSettings.numberOfGroups);
      setSuggestedGroups(groups);
      setIsAnalyzing(false);
    }, 1500);
  }, [state.students, state.assessmentType, navigate]);

  const handleSettingsChange = (key, value) => {
    const newSettings = { ...groupSettings, [key]: value };
    setGroupSettings(newSettings);
    dispatch({ type: 'SET_GROUP_SETTINGS', payload: newSettings });

    // Recreate groups with new settings
    const groups = createGroups(analyzedStudents, newSettings.numberOfGroups);
    setSuggestedGroups(groups);
  };

  const proceedToPlan = () => {
    dispatch({ type: 'SET_GROUPS', payload: suggestedGroups });
    navigate('/plan');
  };

  const openShop = () => {
    window.open("https://shop.thriveedservices.com/", "_blank");
  };

  if (isAnalyzing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 border-4 border-thrive-mint border-t-thrive-teal rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-thrive-slate mb-2 font-display">Analyzing Student Skills...</h2>
          <p className="text-gray-600">Finding each student's lowest unmastered skill</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white shadow-sm border-b"
      >
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/assessment')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} />
              Back to Assessment
            </button>
            <h1 className="text-xl font-semibold text-thrive-slate font-display">Step 2: Group Formation</h1>
            <div className="w-20"></div>
          </div>
        </div>
      </motion.header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Settings Panel */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-1"
          >
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 sticky top-8">
              <h3 className="text-lg font-semibold text-thrive-slate mb-4 flex items-center gap-2 font-display">
                <SafeIcon icon={FiSettings} className="text-thrive-teal" />
                Group Settings
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Groups
                  </label>
                  <select
                    value={groupSettings.numberOfGroups}
                    onChange={(e) => handleSettingsChange('numberOfGroups', parseInt(e.target.value))}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-thrive-teal"
                  >
                    <option value={3}>3 Groups</option>
                    <option value={4}>4 Groups</option>
                    <option value={5}>5 Groups</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Session Minutes
                  </label>
                  <select
                    value={groupSettings.sessionMinutes}
                    onChange={(e) => handleSettingsChange('sessionMinutes', parseInt(e.target.value))}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-thrive-teal"
                  >
                    <option value={15}>15 minutes</option>
                    <option value={20}>20 minutes</option>
                    <option value={25}>25 minutes</option>
                    <option value={30}>30 minutes</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Special Considerations
                  </label>
                  <textarea
                    value={groupSettings.specialConsiderations}
                    onChange={(e) => handleSettingsChange('specialConsiderations', e.target.value)}
                    placeholder="Any student pairings to avoid or priority groups..."
                    className="w-full p-2 border border-gray-300 rounded-lg resize-none h-20 focus:ring-2 focus:ring-thrive-teal"
                  />
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  <p className="mb-2"><strong>{state.students.length}</strong> total students</p>
                  <p><strong>{state.assessmentType}</strong> assessment used</p>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={openShop}
                  className="w-full bg-thrive-peach hover:bg-thrive-rose text-thrive-slate px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiShoppingBag} />
                  Get SOR Resources
                </button>
              </div>
            </div>
          </motion.div>

          {/* Groups Display */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-2"
          >
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-thrive-slate mb-2 font-display">Suggested Groups</h2>
              <p className="text-gray-600">Students grouped by their lowest unmastered skill</p>
            </div>

            <div className="space-y-6">
              {suggestedGroups.map((group, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:border-thrive-mint transition-all">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-thrive-slate flex items-center gap-2 font-display">
                      <div className="bg-thrive-mint/30 p-2 rounded-lg">
                        <SafeIcon icon={FiUsers} className="text-thrive-teal" />
                      </div>
                      Group {index + 1}
                    </h3>
                    <span className="text-sm text-gray-500">{group.students.length} students</span>
                  </div>

                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <SafeIcon icon={FiTarget} className="text-thrive-rose" />
                      <span className="font-medium text-thrive-slate">Skill Focus:</span>
                    </div>
                    <div className="bg-thrive-peach/20 p-3 rounded-lg">
                      <p className="font-medium text-thrive-slate">{group.skillFocus}</p>
                      <p className="text-sm text-gray-600 mt-1">{group.scopeReference}</p>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h4 className="font-medium text-thrive-slate mb-2">Students:</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {group.students.map((student, studentIndex) => (
                        <div key={studentIndex} className="bg-gray-50 p-2 rounded text-sm">
                          <div className="font-medium">{student.name}</div>
                          <div className="text-gray-600 text-xs">{student.assessmentDetail}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-thrive-slate mb-2">Suggested Activities:</h4>
                    <ul className="text-sm text-gray-700 space-y-1">
                      {group.activities.map((activity, actIndex) => (
                        <li key={actIndex} className="flex items-start gap-2">
                          <span className="text-thrive-teal mt-0.5">•</span>
                          {activity}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex justify-end">
              <button
                onClick={proceedToPlan}
                className="bg-thrive-teal hover:bg-thrive-slate text-white px-8 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 shadow-lg"
              >
                Generate Teaching Plan
                <SafeIcon icon={FiArrowRight} />
              </button>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
}

export default GroupPlanner;